import { useState, useEffect } from 'react';
import { ThumbsUpIcon, ThumbsDownIcon } from '../activity-details/icons';
import FeedbackButton from '../activity-details/FeedbackButton';
import { EXPERT_TIPS_COLORS, EXPERT_TIPS_TYPOGRAPHY } from './constants';
import type { FeedbackType } from '../../types/expert-tip.types';

interface TipFeedbackSectionProps {
  tipId: number;
  initialFeedback: FeedbackType | null;
  onFeedbackSubmit: (tipId: number, feedbackType: FeedbackType) => Promise<void>;
  isSubmitting: boolean;
}

type FeedbackState = {
  submitted: boolean;
  type: 'positive' | 'negative' | null;
};

export default function TipFeedbackSection({
  tipId,
  initialFeedback,
  onFeedbackSubmit,
  isSubmitting,
}: TipFeedbackSectionProps) {
  const [feedback, setFeedback] = useState<FeedbackState>({
    submitted: initialFeedback !== null,
    type:
      initialFeedback === 'LIKE' ? 'positive' : initialFeedback === 'DISLIKE' ? 'negative' : null,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFeedback({
      submitted: initialFeedback !== null,
      type:
        initialFeedback === 'LIKE'
          ? 'positive'
          : initialFeedback === 'DISLIKE'
            ? 'negative'
            : null,
    });
  }, [initialFeedback]);

  const handleFeedback = async (type: 'positive' | 'negative') => {
    const feedbackType: FeedbackType = type === 'positive' ? 'LIKE' : 'DISLIKE';
    try {
      setError(null);
      await onFeedbackSubmit(tipId, feedbackType);
      setFeedback({ submitted: true, type });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h3
        className={`font-bold ${EXPERT_TIPS_TYPOGRAPHY.feedbackTitle}`}
        style={{
          fontFamily: 'DM Sans, sans-serif',
          letterSpacing: '-0.72px',
          color: EXPERT_TIPS_COLORS.textPrimary,
          margin: 0,
          lineHeight: 1,
        }}
      >
        Was this tip helpful?
      </h3>

      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <FeedbackButton
            icon={<ThumbsUpIcon size={24} color={EXPERT_TIPS_COLORS.textPrimary} />}
            label="It was great!"
            onClick={() => handleFeedback('positive')}
            isSelected={feedback.type === 'positive'}
            disabled={isSubmitting}
          />
          <FeedbackButton
            icon={<ThumbsDownIcon size={24} color={EXPERT_TIPS_COLORS.textPrimary} />}
            label="Didn't work"
            onClick={() => handleFeedback('negative')}
            isSelected={feedback.type === 'negative'}
            disabled={isSubmitting}
          />
        </div>

        {error && (
          <p
            className={`font-medium ${EXPERT_TIPS_TYPOGRAPHY.thankYouMessage}`}
            style={{ fontFamily: 'DM Sans, sans-serif', color: '#DC2626', margin: 0 }}
          >
            {error}
          </p>
        )}

        <p
          className={`font-medium pb-4 ${EXPERT_TIPS_TYPOGRAPHY.thankYouMessage}`}
          style={{
            fontFamily: 'DM Sans, sans-serif',
            color: EXPERT_TIPS_COLORS.textSecondary,
            opacity: feedback.submitted && !error ? 1 : 0,
            transition: 'opacity 0.3s ease',
            margin: 0,
          }}
        >
          Thank you! Your feedback helps us improve recommendations
        </p>
      </div>
    </div>
  );
}
